import type { ReactNode } from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { InfiniteScroll } from "../../../../lib/pagination";
import { InfiniteScrollSentinel } from "../../layout/InfiniteScrollSentinel";
import { gaps } from "../../spacing";

type ColumnSpan = { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

interface InfiniteGridProps<Item> {
  items: Item[];
  getKey: (item: Item) => string;
  renderItem: (item: Item) => ReactNode;
  /** The loading state of the list, as a hook returns it. */
  scroll: InfiniteScroll;
  /** How many of the 12 columns an item spans at each breakpoint. */
  itemSpan?: ColumnSpan;
}

const DEFAULT_SPAN: ColumnSpan = { xs: 12, sm: 6, lg: 4 };

/**
 * A grid of items that goes on loading as the reader scrolls to its end: it shows the items, says
 * when more are loading, and offers a retry when loading more failed. It knows nothing of what the
 * items are; the list's loading state comes in as one `scroll` object.
 */
export function InfiniteGrid<Item>({
  items,
  getKey,
  renderItem,
  scroll,
  itemSpan = DEFAULT_SPAN,
}: InfiniteGridProps<Item>) {
  return (
    <>
      <Grid container spacing={gaps.base}>
        {items.map((item) => (
          <Grid key={getKey(item)} size={itemSpan}>
            {renderItem(item)}
          </Grid>
        ))}
      </Grid>
      <InfiniteScrollSentinel
        onReach={scroll.onReachEnd}
        disabled={!scroll.hasMore || scroll.loadingMore}
      />
      {scroll.loadingMore ? (
        <Stack direction="row" gap={gaps.snug} justifyContent="center">
          <Typography variant="caption" color="text.secondary">
            Loading more…
          </Typography>
        </Stack>
      ) : null}
      {scroll.error ? (
        <Stack direction="row" gap={gaps.snug} justifyContent="center" alignItems="center">
          <Typography variant="caption" color="error">
            Couldn't load more.
          </Typography>
          <Button size="small" onClick={scroll.onReachEnd}>
            Try again
          </Button>
        </Stack>
      ) : null}
    </>
  );
}
